import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ConditionalDisplayRuleBuildParams } from 'api/LoanOriginationSystem/Base/ConditionalDisplayRuleApi';
import { BorrowerVariableConfiguration } from 'api/LoanOriginationSystem/BorrowerVariableConfigurationsApi';
import BorrowerVariablesConfigurationHeader from './BorrowerProfilesConfigurationHeader';
import {
  createBorrowerVariableConfiguration,
  deleteBorrowerVariableConfiguration,
  updateConditionalDisplayRule,
  updateBorrowerVariableConfigurationPosition,
  updateBorrowerVariableConfigurationRequiredAttribute,
} from 'BorrowerVariableConfigurations/Thunks';
import { formatDisplayTitle } from 'LoanOriginationSystemBorrowers/utils';
import { BorrowerType } from 'api/LoanOriginationSystem/LoanOriginationSystemBorrowersApi';
import { ReduxState } from 'types/redux';
import { TabSwitchOption } from 'components/TabSwitch';
import ViewLayoutConfiguration from 'components/LoanOriginationSystem/ViewLayoutConfiguration';
import { Variable } from 'Variables/VariablesTypes';
import {
  createBorrowerProfileCard,
  deleteBorrowerProfileCard,
  getBorrowerProfileCards,
  updateBorrowerProfileCard,
} from 'BorrowerProfileCards/Thunks';
import { getBorrowerProfileCardsSelector } from 'BorrowerProfileCards/Selectors';

const Tabs: TabSwitchOption[] = [
  { label: 'Person', id: BorrowerType.Person },
  { label: 'Company', id: BorrowerType.Company },
];

export interface BorrowerProfilesConfigurationProps {
  borrowerType: BorrowerType;
  onBorrowerTypeChange: (borrowerType: BorrowerType) => void;
  onDownloadApiTemplate: () => void;
}

const BORROWER_PREFIX = 'borrower';

const BorrowerProfilesConfiguration = ({
  borrowerType,
  onBorrowerTypeChange,
  onDownloadApiTemplate,
}: BorrowerProfilesConfigurationProps) => {
  const dispatch = useDispatch();
  const cards = useSelector((state: ReduxState) => getBorrowerProfileCardsSelector(state, borrowerType));

  useEffect(() => {
    dispatch(getBorrowerProfileCards(borrowerType));
  }, [borrowerType]);

  const handleCardAdd = useCallback(async (position: number, row: number) => {
    await dispatch(createBorrowerProfileCard({
      position,
      row,
      borrowerType,
      name: 'New Card',
    }));
  }, []);

  const handleCardNameUpdate = useCallback(async (cardId: string, name: string) => {
    dispatch(updateBorrowerProfileCard({
      id: cardId,
      name,
    }));
  }, []);

  const handleCardDelete = useCallback(async (cardId: string) => {
    await dispatch(deleteBorrowerProfileCard(cardId));
  }, []);

  const handleVariableConfigurationPositionUpdate = useCallback(async (
    variableConfiguration: BorrowerVariableConfiguration,
    targetCardId: string,
    sourceCardId: string,
    position: number,
    column: number,
  ) => {
    await dispatch(updateBorrowerVariableConfigurationPosition({
      id: variableConfiguration.id,
      column,
      position,
      cardId: targetCardId,
      sourceCardId,
    }));
  }, []);

  const handleVariableConfigurationAdd = useCallback(async (cardId: string, column: number, position: number, variable: Variable) => {
    await dispatch(createBorrowerVariableConfiguration({
      borrowerType,
      column,
      position,
      variable: variable.id,
      cardId,
    }));
  }, []);

  const handleVariableConfigurationDelete = useCallback(async (variableConfiguration: BorrowerVariableConfiguration) => {
    await dispatch(deleteBorrowerVariableConfiguration(variableConfiguration.id));
  }, []);

  const handleChangeVariableConfigurationAttribute = useCallback((variableConfiguration: BorrowerVariableConfiguration, required: boolean) => {
    dispatch(updateBorrowerVariableConfigurationRequiredAttribute({
      id: variableConfiguration.id,
      required,
    }));
  }, []);

  const handleEditVariableConfigurationDisplayRule = useCallback(
    async ({ id }: BorrowerVariableConfiguration, conditionalDisplayRule: ConditionalDisplayRuleBuildParams | null) => {
      await dispatch(updateConditionalDisplayRule({
        id,
        conditionalDisplayRule,
      }))
    },
    [],
  );

  const getTitle = () => {
    const tab = Tabs.find(({ id }) => id === borrowerType);

    if (!tab) {
      return '';
    }

    return tab.label;
  };

  const getListVariableAttributes = useCallback((variable: Variable) => {
    const isBorrowerVariable = variable.systemName.startsWith(BORROWER_PREFIX);

    return {
      disabled: !isBorrowerVariable,
      tooltip: !isBorrowerVariable ? (
        <>
          <p>Only variables with system names</p>
          <p>that start with “borrower” can be</p>
          <p>added to the Borrower Profile</p>
        </>
      ) : null,
    };
  }, []);

  return (
    <>
      <BorrowerVariablesConfigurationHeader
        onChangeBorrowerType={onBorrowerTypeChange}
        onDownloadApiTemplate={onDownloadApiTemplate}
        borrowerType={borrowerType}
        tabs={Tabs}
      />
      <ViewLayoutConfiguration
        title={getTitle()}
        cards={cards}
        onCardAdd={handleCardAdd}
        onCardNameUpdate={handleCardNameUpdate}
        onCardDelete={handleCardDelete}
        onVariableConfigurationPositionUpdate={handleVariableConfigurationPositionUpdate}
        onVariableConfigurationCreate={handleVariableConfigurationAdd}
        onVariableConfigurationDelete={handleVariableConfigurationDelete}
        onEditVariableConfigurationConditionalDisplayRule={handleEditVariableConfigurationDisplayRule}
        formatVariableConfigurationDisplayTitle={formatDisplayTitle}
        onChangeVariableConfigurationRequiredAttribute={handleChangeVariableConfigurationAttribute}
        allowStandardVariables
        getListVariableAttributes={getListVariableAttributes}
      />
    </>
  );
};

export default BorrowerProfilesConfiguration;
